/**
 * Onboarding Tooltip Pointer
 *
 * A dark floating tooltip with a diagonal SVG connector line pointing to a
 * specific element on the page. Each tooltip is locked to a horizontal "slot"
 * and can have an independent vertical offset so multiple tooltips never
 * overlap — regardless of scroll position or anchor location.
 *
 * Feature: Onboarding
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@insio/ui-toolkit';

/** localStorage key prefix for dismissed state */
const STORAGE_PREFIX = 'onboarding_tooltip_dismissed_';

/** Vertical gap between the anchor's bottom edge and the tooltip's top (px) */
const VERTICAL_GAP = 32;

/** Stroke width for SVG connector lines */
const LINE_STROKE = 1.6;

/** Line color matching the tooltip background */
const LINE_COLOR = 'black';

/** Dot radius drawn at the anchor end of the connector (px) */
const DOT_RADIUS = 2.2;

/** Margin from viewport edges (px) */
const EDGE_MARGIN = 12;

/**
 * Horizontal slot — controls where the tooltip is placed relative to its anchor.
 *
 * - `left`   — tooltip placed to the LEFT of the anchor center
 * - `center` — tooltip centered on the anchor
 * - `right`  — tooltip placed to the RIGHT of the anchor center
 */
export type TooltipSlotX = 'left' | 'center' | 'right';

export interface OnboardingTooltipProps {
  /** Unique key used for localStorage persistence */
  storageKey: string;
  /** CSS selector for the anchor element to point to */
  anchorSelector: string;
  /** Title text (displayed in secondary/pink color) */
  title: string;
  /** Body text (optional, displayed in white below title) */
  text?: string;
  /** Whether the tooltip system is active */
  active: boolean;
  /** Horizontal placement relative to the anchor */
  slotX?: TooltipSlotX;
  /**
   * Extra vertical offset (px) added on top of VERTICAL_GAP.
   * Use to stagger tooltips that share the same anchor Y (e.g. all in a navbar).
   */
  verticalOffset?: number;
  /** User ID to scope localStorage keys (prevents cross-user leakage) */
  userId?: string;
}

/**
 * Compute the `left` CSS value for the tooltip box.
 * `left` slot: tooltip right-edge aligns near anchor — tooltip is to the LEFT.
 * `center` slot: tooltip is centered on the anchor.
 * `right` slot: tooltip left-edge aligns near anchor — tooltip is to the RIGHT.
 */
function slotToLeft(slot: TooltipSlotX, anchorCenterX: number, tooltipWidth: number): number {
  switch (slot) {
    case 'left':
      // Tooltip ends near the anchor — sits to the left
      return anchorCenterX - tooltipWidth + tooltipWidth * 0.25;
    case 'center':
      return anchorCenterX - tooltipWidth / 2;
    case 'right':
      // Tooltip starts near the anchor — sits to the right
      return anchorCenterX - tooltipWidth * 0.25;
  }
}

/**
 * Dark floating tooltip with diagonal SVG connector line.
 */
export function OnboardingTooltip({
  storageKey,
  anchorSelector,
  title,
  text,
  active,
  slotX = 'center',
  verticalOffset = 0,
  userId
}: OnboardingTooltipProps) {
  const fullKey = userId
    ? `${STORAGE_PREFIX}${storageKey}_${userId}`
    : `${STORAGE_PREFIX}${storageKey}`;
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(fullKey) === 'true');
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    anchorX: number;
    anchorY: number;
  } | null>(null);
  const [visible, setVisible] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    const anchor = document.querySelector(anchorSelector);
    if (!anchor) {
      setPosition(null);
      return;
    }

    const rect = anchor.getBoundingClientRect();
    const tooltip = tooltipRef.current;
    const tooltipWidth = tooltip?.offsetWidth ?? 192;
    const tooltipHeight = tooltip?.offsetHeight ?? 60;

    // Anchor point: bottom-center of the anchor element
    const anchorX = rect.left + rect.width / 2;
    const anchorY = rect.bottom;

    // Y: below the anchor + gap + extra stagger offset
    let top = anchorY + VERTICAL_GAP + verticalOffset;

    // X: near the anchor, nudged by slot direction
    let left = slotToLeft(slotX, anchorX, tooltipWidth);

    // Clamp to viewport
    left = Math.max(EDGE_MARGIN, Math.min(left, window.innerWidth - tooltipWidth - EDGE_MARGIN));
    top = Math.max(EDGE_MARGIN, Math.min(top, window.innerHeight - tooltipHeight - EDGE_MARGIN));

    setPosition({ top, left, anchorX, anchorY });
  }, [anchorSelector, slotX, verticalOffset]);

  // Re-measure after first render so tooltipRef.current has real dimensions
  useEffect(() => {
    if (dismissed || !active) return;

    let rafId: number;
    const showTimer = setTimeout(() => {
      updatePosition();
      // Second pass — tooltip is now mounted, get real dimensions
      rafId = requestAnimationFrame(() => {
        updatePosition();
        setVisible(true);
      });
    }, 500);

    const handleResize = () => updatePosition();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);

    return () => {
      clearTimeout(showTimer);
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
    };
  }, [dismissed, active, updatePosition]);

  const handleDismiss = () => {
    setVisible(false);
    setDismissed(true);
    localStorage.setItem(fullKey, 'true');
  };

  if (dismissed || !active || !position || typeof document === 'undefined') return null;

  // Portal to body so overflow/scroll/transform ancestors cannot clip, detach,
  // or under-stack the tooltip relative to the app header (which is also z-50).
  return createPortal(
    <>
      {/* SVG diagonal connector line from anchor to tooltip */}
      <ConnectorLine
        anchorX={position.anchorX}
        anchorY={position.anchorY}
        tooltipTop={position.top}
        tooltipLeft={position.left}
        tooltipRef={tooltipRef}
        visible={visible}
      />

      {/* Tooltip card */}
      <div
        ref={tooltipRef}
        className={`fixed z-60 max-w-48 rounded-lg bg-black p-sm shadow-xl transition-opacity duration-300 ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ top: position.top, left: position.left }}
      >
        {/* Close button shares a row with the title so it lands in the card's
            top-right padding box, aligned with the title's first line */}
        <div className='flex items-start justify-between gap-xs'>
          <p className='text-xs font-semibold text-secondary-base'>{title}</p>
          <button
            type='button'
            onClick={handleDismiss}
            className='flex h-4 w-4 shrink-0 items-center justify-center text-white/60 hover:text-white transition-colors'
            aria-label='Close'
          >
            <Icon name='xmark' size='sm' />
          </button>
        </div>
        {text && <p className='text-[11px] leading-tight text-white/80 mt-xs'>{text}</p>}
      </div>
    </>,
    document.body
  );
}

// ── SVG connector line ──────────────────────────────────────────────────────

interface ConnectorLineProps {
  anchorX: number;
  anchorY: number;
  tooltipTop: number;
  tooltipLeft: number;
  tooltipRef: React.RefObject<HTMLDivElement | null>;
  visible: boolean;
}

/**
 * Renders an SVG diagonal line from the anchor bottom-center to the
 * nearest horizontal edge of the tooltip (top-left, top-center, or top-right),
 * with a small dot at the anchor end.
 */
function ConnectorLine({
  anchorX,
  anchorY,
  tooltipTop,
  tooltipLeft,
  tooltipRef,
  visible
}: ConnectorLineProps) {
  const tooltipWidth = tooltipRef.current?.offsetWidth ?? 192;

  const tooltipRight = tooltipLeft + tooltipWidth;
  const tooltipCenterX = tooltipLeft + tooltipWidth / 2;

  // Connect to whichever horizontal point on the tooltip top edge is closest to the anchor
  let tooltipX: number;
  if (anchorX < tooltipLeft) {
    // Anchor is to the left of the tooltip — connect to top-left corner area
    tooltipX = tooltipLeft + tooltipWidth * 0.25;
  } else if (anchorX > tooltipRight) {
    // Anchor is to the right of the tooltip — connect to top-right corner area
    tooltipX = tooltipRight - tooltipWidth * 0.25;
  } else {
    // Anchor is above the tooltip — connect to top-center
    tooltipX = tooltipCenterX;
  }
  const tooltipY = tooltipTop;

  // Compute SVG viewBox bounds (with padding for the dot)
  const pad = DOT_RADIUS + 2;
  const minX = Math.min(anchorX, tooltipX) - pad;
  const minY = Math.min(anchorY, tooltipY) - pad;
  const maxX = Math.max(anchorX, tooltipX) + pad;
  const maxY = Math.max(anchorY, tooltipY) + pad;
  const svgWidth = maxX - minX;
  const svgHeight = maxY - minY;

  return (
    <svg
      className={`fixed z-60 pointer-events-none transition-opacity duration-300 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
      style={{
        left: minX,
        top: minY,
        width: svgWidth,
        height: svgHeight
      }}
      viewBox={`${minX} ${minY} ${svgWidth} ${svgHeight}`}
    >
      <line
        x1={anchorX}
        y1={anchorY}
        x2={tooltipX}
        y2={tooltipY}
        stroke={LINE_COLOR}
        strokeWidth={LINE_STROKE}
        strokeLinecap='round'
      />
      {/* Dot at anchor end */}
      <circle cx={anchorX} cy={anchorY} r={DOT_RADIUS} fill={LINE_COLOR} />
    </svg>
  );
}
